import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Box,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  SelectChangeEvent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import ImageDropZone from '../../../common/widgets/file-picker';
import { CREATE_TOOL } from '../../gql';
import { errorToast, successToast } from '../../../common/utils/alert';
import { errorMessages } from '../../../common/utils/constants';
import { TabInfo } from '../../../common/utils/interfaces';
import { Form, Formik } from 'formik';
import { FileUpload } from '../../type';
import { useAppSelector } from '../../../../redux/hooks';

interface AddToolsAndTechProps {
  id: string;
  open: boolean;
  comment: string;
  defaultDepartment: string;
  handleClose: () => void;
  createToolCallback: (toolId: string) => void;
  tabs: TabInfo[];
}

const AddToolsAndTechForm: React.FC<AddToolsAndTechProps> = ({
  id,
  open,
  comment,
  defaultDepartment,
  handleClose,
  createToolCallback,
  tabs
}) => {
  const [tool, setTool] = useState<string | null>(null);
  const [validator, setValidator] = useState<{
    id: string;
    displayName: string;
  } | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<FileUpload[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>(
    tabs[0].label
  );

  const [errors, setErrors] = useState<{
    toolError?: boolean;
    departmentError?: boolean;
    validatorError?: boolean;
    imageError?: boolean;
  }>({
    toolError: false,
    departmentError: false,
    validatorError: false,
    imageError: false
  });

  useEffect(() => {
    setSelectedDepartment(defaultDepartment);
  }, [defaultDepartment]);

  const profileData = useAppSelector((state) => state.profileData.profileData);

  const [createToolMutation] = useMutation(CREATE_TOOL);

  const handleChange = (event: SelectChangeEvent): void => {
    setSelectedDepartment(event.target.value);
  };

  const close = (): void => {
    setTool(null);
    setValidator(null);
    setUploadedFiles([]);
    setErrors({
      toolError: false,
      departmentError: false,
      validatorError: false,
      imageError: false
    });
    handleClose();
  };

  async function getBase64(file: File): Promise<string | ArrayBuffer | null> {
    const reader = new FileReader();
    reader.readAsDataURL(file as unknown as File);

    return await new Promise((resolve, reject) => {
      reader.onloadend = () => {
        resolve(reader.result);
      };
    });
  }

  const handleAdd = async (): Promise<void> => {
    if (!tool || !selectedDepartment) {
      setErrors({
        toolError: !tool,
        departmentError: !selectedDepartment,
        validatorError: !validator
      });
      return;
    }

    let imageToUrl;
    if (uploadedFiles[0]) {
      imageToUrl = await getBase64(uploadedFiles[0] as unknown as File);
    }

    createToolMutation({
      variables: {
        name: tool,
        department: selectedDepartment,
        image: imageToUrl ?? '',
        createdById: profileData?.id
      }
    })
      .then((response) => {
        successToast('A new tool/technology added');
        const createdSkill = JSON.parse(response.data.createSkill);
        createToolCallback(createdSkill.id);
      })
      .catch(() => {
        errorToast(errorMessages.ErrorMessageCreatingTools);
      });

    close();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: '28px',
          backgroundColor: '#f4f2fd',
          padding: '24px',
          width: '60%'
        }
      }}
    >
      <DialogContent sx={{ padding: '0' }}>
        <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
          <DialogContentText
            sx={{
              display: 'flex',
              fontSize: '24px',
              textAlign: 'center',
              color: '#1A1B1F',
              marginTop: '12px',
              flexWrap: 'wrap',
              wordWrap: 'break-word'
            }}
          >
            Add New Tool or Technology
          </DialogContentText>
          <IconButton onClick={close} sx={{ height: '24px', width: '24px' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Formik
          onSubmit={handleAdd}
          initialValues={{ tool, selectedDepartment, uploadedFiles }}
        >
          {({ setFieldValue, touched, values }) => (
            <Form className={'form'} data-testid="form">
              <Box
                sx={{
                  marginTop: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px'
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: '#44474E'
                    }}
                  >
                    Tool/Technology/Competency
                  </Typography>
                  <TextField
                    value={tool}
                    onChange={(e) => {
                      setTool(e.target.value);
                      setErrors({ ...errors, toolError: false });
                    }}
                  />
                  {errors.toolError && (
                    <Typography
                      sx={{
                        color: 'red',
                        fontSize: '10px'
                      }}
                    >
                      Tool Name is required.
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}
                >
                  <Typography>Department</Typography>
                  <Select
                    labelId="select-label"
                    value={selectedDepartment}
                    onChange={handleChange}
                  >
                    {tabs.map((tab) => (
                      <MenuItem key={tab.label} value={tab.label}>
                        {tab.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.departmentError && !selectedDepartment && (
                    <Typography
                      sx={{
                        color: 'red',
                        fontSize: '10px'
                      }}
                    >
                      Department is required.
                    </Typography>
                  )}
                </Box>
                <Box
                  sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Be Vietnam Pro',
                      fontSize: '16px',
                      fontWeight: 500,
                      lineHeight: '24px'
                    }}
                  >
                    Image
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      border: 1,
                      borderRadius: '4px',
                      borderColor: '#74777F'
                    }}
                  >
                    <ImageDropZone
                      setUploadedFiles={setUploadedFiles}
                      uploadedFiles={uploadedFiles}
                      setErrors={setErrors}
                    />
                  </Box>
                  {errors.imageError && (
                    <Typography
                      sx={{
                        color: 'red',
                        fontSize: '10px'
                      }}
                    >
                      Image Should be Less than 1mb & should be file type of png
                      or jpeg
                    </Typography>
                  )}
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <Button
          sx={{
            textTransform: 'none',
            fontSize: '18px',
            color: '#295DAB',
            fontWeight: '500'
          }}
          onClick={close}
        >
          Cancel
        </Button>

        <Button
          sx={{
            textTransform: 'none',
            fontSize: '18px',
            color: '#295DAB',
            fontWeight: '500'
          }}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => await handleAdd()}
          autoFocus
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToolsAndTechForm;
